import type { Transacao, ContaComSaldo, TipoTransacao } from '@pgcustom/shared-types';
import { reaisParaCentavos } from '@pgcustom/shared-utils';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { FinanceiroApi } from './financeiro.api.js';

/**
 * `<erp-transacao-form-modal>` — lançamento manual de transação
 * (despesa fixa, pró-labore, aporte — `PRODUCT.md`, 3.4). Mesmo padrão
 * de `erp-cliente-form-modal`: dependências injetadas via propriedade
 * JS, `pg-modal` + `pg-button`. Sem modo de edição — transação, uma vez
 * lançada, nunca é alterada nem excluída por este componente (o
 * livro-razão é sempre um registro de fatos, nunca um cadastro mutável).
 */
export class ErpTransacaoFormModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _financeiroApi: FinanceiroApi | null = null;
  private _aoSalvar: ((transacao: Transacao) => void) | null = null;

  private aberto = false;
  private contas: ContaComSaldo[] = [];
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set financeiroApi(valor: FinanceiroApi) {
    this._financeiroApi = valor;
  }

  set aoSalvar(valor: (transacao: Transacao) => void) {
    this._aoSalvar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  abrirParaCriar(contas: ContaComSaldo[]): void {
    this.contas = contas;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._financeiroApi || this.enviando) return;

    const contaId = this.valorCampo('#conta');
    const tipo = this.valorCampo('#tipo') as TipoTransacao;
    const valorReais = Number(this.valorCampo('#valor').replace(',', '.'));
    const descricao = this.valorCampo('#descricao').trim();

    if (!contaId) {
      this.mensagemErro = 'selecione uma conta';
      this.render();
      return;
    }
    if (!Number.isFinite(valorReais) || valorReais <= 0) {
      this.mensagemErro = 'valor deve ser maior que zero';
      this.render();
      return;
    }
    if (!descricao) {
      this.mensagemErro = 'descrição é obrigatória';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const transacao = await this._financeiroApi.criarTransacao({
        contaId,
        tipo,
        valorCentavos: reaisParaCentavos(valorReais),
        descricao,
      });
      this.aberto = false;
      this._aoSalvar?.(transacao);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement | HTMLSelectElement>(seletor)?.value ?? '';
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Novo lançamento</span>
        <form novalidate>
          <label>
            Conta
            <select id="conta">
              ${this.contas.map((c) => `<option value="${c.id}">${escaparHtml(c.nome)}</option>`).join('')}
            </select>
          </label>
          <label>
            Tipo
            <select id="tipo">
              <option value="despesa">Despesa</option>
              <option value="receita">Receita</option>
            </select>
          </label>
          <label>
            Valor (R$)
            <input id="valor" type="text" inputmode="decimal" placeholder="0,00" />
          </label>
          <label>
            Descrição
            <input id="descricao" type="text" maxlength="255" placeholder="ex: aluguel, pró-labore, aporte" />
          </label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Salvar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-transacao-form-modal', ErpTransacaoFormModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-transacao-form-modal': ErpTransacaoFormModal;
  }
}
